import { Box, Grid, Slide, Typography } from "@mui/material";
import {
  DEFAULT_HIDEABLES,
  DEFAULT_ROWS,
} from "codes";
import { useUpdateUserMutation } from "api/api-users";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import CLDisplay from "components/CLRequest/CLDisplay";
import SourceDisplay from "components/CLRequest/SourceDisplay";
import { formatCLs } from "helpers/formatCLs";
import { useGetCreditLimitsQuery } from "api/api-origination";
import DataTablev2 from "components/Common/DataTablev2";
import Layout from "components/Common/Layout";
import NoData from "components/Common/NoData";
import SkeletonLoad from "components/Common/SkeletonLoad";
import { currencyFormatter } from "helpers/currencyFormatter";
import history from "helpers/history";
import useWidth from "helpers/useWidth";
import useIsMobile from "hooks/useIsMobile";
import moment from "moment";
import { PERMISSIONS } from "permissions";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import type { CLDetails } from "types";
import { clearAll } from "redux/reducers/searchReducer";

const CreditLimits = () => {
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const width = ref ? useWidth(ref) : 1000;
  const searchValue = useAppSelector((state) => state.search.value);
  const [queryParams, setQueryParams] = useState({
    per_page: 10,
    page: 1,
    search: searchValue || undefined,
  });
  const [filters, setFilters] = useState<Record<string, any>>({});

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(clearAll());
  }, [dispatch]);

  const hasSearchedOnce = useRef(false);
  useEffect(() => {
    if (searchValue && !hasSearchedOnce.current) {
      hasSearchedOnce.current = true;
    }
    if (hasSearchedOnce.current) {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        search: searchValue || undefined,
      }));
    }
  }, [searchValue]);

  const { data: curr_user } = useGetCurrentUserQuery();
  const [updateUser] = useUpdateUserMutation();
  const APP_PERMISSION = useAppSelector((state) => state.appState.permission);
  const USER_PERMISSION = useAppSelector(
    (state) => state.appState.user_permission
  );

  const {
    data: creditLimits,
    isFetching,
    refetch,
  } = useGetCreditLimitsQuery(
    {
      creditlimitsparams: `?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(queryParams).filter(
            ([_, value]) => value !== undefined
          )
        ) as Record<string, string>
      )}`,
    },
    {
      pollingInterval: 300000,
      refetchOnReconnect: true,
    }
  );

  const handleClearFilters = () => {
    setQueryParams({
      per_page: 10,
      page: 1,
      search: undefined,
    });
    setFilters({});
    dispatch(clearAll());
    refetch();
  };

  const handleRefetch = async () => {
    await refetch();
    setQueryParams({
      per_page: 10,
      page: 1,
      search: undefined,
    });
    dispatch(clearAll());
    setFilters({});
  };

  const handleFilterSubmit = (filtersString: string) => {
    const filtersObj = JSON.parse(filtersString);
    setFilters(filtersObj);
    const newFilters: Record<string, string> = {};

    for (const key in filtersObj) {
      if (filtersObj[key]?.keywords) {
        if (
          key === "created_at" &&
          typeof filtersObj[key].keywords === "string"
        ) {
          newFilters[key] = filtersObj[key].keywords;
        } else if (Array.isArray(filtersObj[key].keywords)) {
          newFilters[key] = filtersObj[key].keywords.join(",");
        }
      }
    }

    setQueryParams((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const settings = curr_user?.settings ?? {};
  const rowsPerPage = settings?.rowsPerPage ?? DEFAULT_ROWS;
  const ComponentLevelSetting = settings?.[APP_PERMISSION]?.[history.location.pathname];
  const presets = ComponentLevelSetting?.hideablesPreset ?? DEFAULT_HIDEABLES;

  const toBeHidden = [
    "id",
    "item",
    "recordId",
    "insurerResponse",
    "salesLead",
    "currency",
    "buyerId",
    "comment",
    "buyerEmail",
    "buyerAnnualRevenue",
    "buyerIsIdentified",
    "dateRequestedMktg",
    "clStatus",
    "insurer",
    "atradius_comment",
    "coface_currency",
    "sf_link",
    "requestedBy",
    "rating",
    "buyerInternalName",
    "insuredAmount",
    "coface_rating_description",
    "coface_amount",
    "coface_found",
  ];

  const toBeHiddenForRM = [...toBeHidden];

  const textSx = {
    fontSize: "1.1em!important",
    lineHeight: "1.5em",
    height: "auto",
    overflow: "hidden",
    maxWidth: "100%",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    wordWrap: "break-word",
    minWidth: isMobile ? "100%" : "10vw",
  };

  const formattedData = formatCLs(creditLimits?.creditlimit_details);
  const defaultColumnWidth = width ? (width / 11) * 1.2 : 200;

  const handlePageChange = (newPage: number, newPerPage: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: newPage,
      per_page: newPerPage,
    }));
  };

  return (
    <Slide in direction="left">
      <Box sx={{ flexGrow: 1, p: 0 }}>
        <Layout
          sx={{ padding: "1px" }}
          title="Credit Limits"
          textHeadingOptions={{
            fontWeight: "bolder",
            level: 2,
            fontSize: isMobile ? "1.2em" : "1.5em",
          }}
          headerConfig={{
            syncAction: handleRefetch,
            left: 4,
            mid: 0,
            right: 4,
            xs: {
              left: 6,
              mid: 0,
              right: 6,
            },
            alignItems: "center",
          }}
          mode="default"
        >
          <Grid item xs={12} ref={ref} sx={{ margin: "5px" }}>
            <Grid container spacing={1}>
              {isFetching && <SkeletonLoad bars={10} />}
              {!isFetching && formattedData && (
                <DataTablev2
                  disableFilters={false}
                  clearFilters={handleClearFilters}
                  onFilterSubmit={handleFilterSubmit}
                  filterColumnTableName={creditLimits}
                  pagination={creditLimits?.pagination}
                  onPaginationChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  defaultColumnWidth={defaultColumnWidth}
                  data={formattedData}
                  toBeHidden={
                    USER_PERMISSION.includes(PERMISSIONS.get_insurer)
                      ? toBeHidden
                      : toBeHiddenForRM
                  }
                  filters={filters}
                  setFilters={setFilters}
                  customColumns={{
                    reqId: {
                      reqId: "CL ID",
                      type: "text",
                      color: "primary.main",
                      flexGrow: 1,
                      link: {
                        href: (params: CLDetails) =>
                          `${history.location.pathname}/${params.reqId}`,
                        target: "_self",
                      },
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={{
                          ...textSx,
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            color: "primary.dark"
                          }
                        }}>{value}</Typography>
                      ),
                    },
                    buyerName: {
                      buyerName: "Buyer Name",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography
                          sx={{
                            ...textSx,
                            maxWidth: isMobile ? "100%" : "200px", // Set a reasonable max width
                            whiteSpace: "normal", // Ensure text wraps
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {value}
                        </Typography>
                      ),
                    },
                    buyerRegNo: {
                      buyerRegNo: "Buyer Reg.No.",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={textSx}>{value}</Typography>
                      ),
                    },
                    buyerCountry: {
                      buyerCountry: "Buyer Country",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={textSx}>{value}</Typography>
                      ),
                    },
                    supplierName: {
                      supplierName: "Supplier Name",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={textSx}>{value}</Typography>
                      ),
                    },
                    supplierCountry: {
                      supplierCountry: "Supplier Country",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={textSx}>{value}</Typography>
                      ),
                    },
                    dateRequested: {
                      dateRequested: "Date Requested",
                      type: "date",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={textSx}>
                          {value ? moment(value).format("DD/MM/YYYY") : "None"}
                        </Typography>
                      ),
                    },
                    salesName: {
                      salesName: "Sales Owner",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={textSx}>{value}</Typography>
                      ),
                    },
                    internalStatus: {
                      internalStatus: "Status",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Grid container justifyContent={isMobile ? "left" : "center"}>
                          <Grid item xs={12}>
                            <CLDisplay status={value} />
                          </Grid>
                        </Grid>
                      ),
                    },
                    requested_amount: {
                      requested_amount: "Requested Amount",
                      type: "number",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: number }) => (
                        <Typography sx={textSx} color={value ? "primary" : "grey"}>
                          {value
                            ? currencyFormatter({ amount: Number(value) })
                            : "None"}
                        </Typography>
                      ),
                    },
                    approved_amount: {
                      approved_amount: "Approved Amount",
                      type: "number",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: number }) => (
                        <Typography sx={textSx} color={value ? "primary" : "grey"}>
                          {value
                            ? currencyFormatter({ amount: Number(value) })
                            : "None"}
                        </Typography>
                      ),
                    },
                    supplierSource: {
                      supplierSource: "Supplier Source",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Grid container justifyContent={isMobile ? "left" : "center"}>
                          <Grid item xs={12}>
                            <SourceDisplay status={value} />
                          </Grid>
                        </Grid>
                      ),
                    },
                    buyerSource: {
                      buyerSource: "Buyer Source",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={textSx}>{value}</Typography>
                      ),
                    },
                    product: {
                      product: "Product Name",
                      type: "text",
                      flexGrow: 1,
                      disableClick: true,
                      renderCell: ({ value }: { value: string }) => (
                        <Typography sx={textSx}>{value}</Typography>
                      ),
                    },
                  }}
                  hideablesPreset={presets}
                  getColumnVisibility={(columns) => {
                    const PRESET_TO_SET = Object.entries(columns)
                      .map((value: [string, any]) => {
                        if (!value[1]) return value[0];
                        return "";
                      })
                      .filter((x) => x !== "");

                    const toUpdate = {
                      id: curr_user?.id,
                      settings: {
                        ...curr_user?.settings,
                        [APP_PERMISSION]: {
                          [history.location.pathname]: {
                            hideablesPreset: PRESET_TO_SET,
                          },
                        },
                      },
                    };

                    if (
                      PRESET_TO_SET?.length !==
                        ComponentLevelSetting?.hideablesPreset?.length &&
                      Boolean(PRESET_TO_SET.length)
                    )
                      updateUser(toUpdate)
                        .unwrap()
                        .catch((err) =>
                          toast(
                            `Unable to save your settings: ${err?.message}`,
                            {
                              type: "error",
                            }
                          )
                        );
                  }}
                  onRowClicked={(row) => {
                    history.push(`${history.location.pathname}/${row?.reqId}`);
                  }}
                />
              )}
              {!isFetching && (!formattedData || formattedData?.length === 0) && (
                <NoData text="No Credit Limits Requests found" sx={{ margin: "auto" }} />
              )}
            </Grid>
          </Grid>
        </Layout>
      </Box>
    </Slide>
  );
};

export default CreditLimits;
